import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, Save, Sparkles, FileDown, FileText, Plus, Trash2, Eye,
} from "lucide-react";

const RESPONSIBILITIES = ["Contractor", "Developer", "Both"];

const ProposalBuilderPage = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [settings, setSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [extraContext, setExtraContext] = useState("");
  const [showEmail, setShowEmail] = useState(false);

  // ----- load -----
  const fetchAll = useCallback(async () => {
    try {
      const [p, s, c] = await Promise.all([
        axios.get(`${API}/proposals/${proposalId}`),
        axios.get(`${API}/proposal-settings`),
        axios.get(`${API}/customers`),
      ]);
      setProposal(p.data);
      setSettings(s.data);
      setCustomers(c.data || []);
      // On first-time open (no checklists yet), seed from library
      if (
        (p.data.pre_construction || []).length === 0 &&
        (p.data.inclusions || []).length === 0 &&
        (p.data.exclusions || []).length === 0 &&
        (p.data.assumptions || []).length === 0
      ) {
        const seeded = {
          ...p.data,
          pre_construction: (s.data.pre_construction_library || []).map((it) => ({ id: it.id, text: it.text, checked: it.default_checked })),
          inclusions: (s.data.inclusions_library || []).map((it) => ({ id: it.id, text: it.text, checked: it.default_checked })),
          exclusions: (s.data.exclusions_library || []).map((it) => ({ id: it.id, text: it.text, checked: it.default_checked })),
          assumptions: (s.data.assumptions_library || []).map((it) => ({ id: it.id, text: it.text, checked: it.default_checked })),
          insurance_text: p.data.insurance_text || s.data.default_insurance_text || "",
        };
        setProposal(seeded);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  }, [proposalId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const update = (patch) => setProposal((p) => ({ ...p, ...patch }));
  const updateNested = (key, idx, patch) => setProposal((p) => {
    const list = [...(p[key] || [])];
    list[idx] = { ...list[idx], ...patch };
    return { ...p, [key]: list };
  });
  const addItem = (key, item) => setProposal((p) => ({ ...p, [key]: [...(p[key] || []), item] }));
  const removeItem = (key, idx) => setProposal((p) => {
    const list = [...(p[key] || [])];
    list.splice(idx, 1);
    return { ...p, [key]: list };
  });

  const totalAmount = useMemo(
    () => (proposal?.line_items || []).reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0),
    [proposal?.line_items]
  );

  // ----- actions -----
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...proposal };
      delete payload.id;
      delete payload.proposal_number;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.ai;
      delete payload.pdf_url;
      const resp = await axios.put(`${API}/proposals/${proposalId}`, payload);
      setProposal(resp.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDraft = async () => {
    // Save first so Claude sees the latest inputs
    setDrafting(true);
    try {
      const payload = { ...proposal };
      delete payload.id; delete payload.proposal_number; delete payload.created_at;
      delete payload.updated_at; delete payload.ai; delete payload.pdf_url;
      await axios.put(`${API}/proposals/${proposalId}`, payload);
      const resp = await axios.post(`${API}/proposals/${proposalId}/ai-draft`, { extra_context: extraContext });
      setProposal((p) => ({ ...p, ai: resp.data.ai }));
      toast.success("Claude drafted your proposal");
      setShowEmail(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const handleDownloadPdf = async () => {
    try {
      await handleSave();
      const resp = await axios.get(`${API}/proposals/${proposalId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF failed");
    }
  };

  const handleDownloadDocx = async () => {
    try {
      await handleSave();
      const resp = await axios.get(`${API}/proposals/${proposalId}/docx`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proposal_${proposal.proposal_number}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("DOCX downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "DOCX failed");
    }
  };

  const handleCustomerSelect = (customerId) => {
    const c = customers.find((x) => x.id === customerId);
    if (!c) return;
    update({
      customer_airtable_id: c.id,
      customer_name: c.name || "",
      customer_email: c.email || "",
      customer_phone: c.phone || "",
      customer_address: c.address || "",
    });
  };

  if (!proposal) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="proposal-builder">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/proposals")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Proposals
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold uppercase tracking-tight text-[#00341d]">
              Edit Proposal
            </h1>
            <p className="text-xs text-zinc-500 font-mono">{proposal.proposal_number}</p>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
              <FileDown className="w-4 h-4 mr-2" /> Preview PDF
            </Button>
            <Button variant="outline" onClick={handleDownloadDocx} data-testid="download-docx-btn">
              <FileText className="w-4 h-4 mr-2" /> Download DOCX
            </Button>
            <Button
              variant="outline"
              onClick={handleDraft}
              disabled={drafting}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="draft-with-claude-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {drafting ? "Drafting..." : "Draft with Claude"}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#00341d] hover:bg-[#002816] text-white" data-testid="save-proposal-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Client + Project */}
        <Card>
          <CardHeader><CardTitle>Client &amp; Project</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Customer (from Airtable)</Label>
              <Select value={proposal.customer_airtable_id || ""} onValueChange={handleCustomerSelect}>
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Select a customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.email ? `— ${c.email}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client Name</Label>
              <Input value={proposal.customer_name || ""} onChange={(e) => update({ customer_name: e.target.value })} data-testid="customer-name-input" />
            </div>
            <div>
              <Label>Client Email</Label>
              <Input value={proposal.customer_email || ""} onChange={(e) => update({ customer_email: e.target.value })} data-testid="customer-email-input" />
            </div>
            <div>
              <Label>Client Phone</Label>
              <Input value={proposal.customer_phone || ""} onChange={(e) => update({ customer_phone: e.target.value })} />
            </div>
            <div>
              <Label>Client Address</Label>
              <Input value={proposal.customer_address || ""} onChange={(e) => update({ customer_address: e.target.value })} />
            </div>
            <div>
              <Label>Project Name</Label>
              <Input value={proposal.project_name || ""} onChange={(e) => update({ project_name: e.target.value })} data-testid="project-name-input" />
            </div>
            <div>
              <Label>Project Location (address / parcel IDs)</Label>
              <Input value={proposal.project_location || ""} onChange={(e) => update({ project_location: e.target.value })} data-testid="project-location-input" />
            </div>
            <div>
              <Label>Project Size (e.g. "6.3 acres")</Label>
              <Input value={proposal.project_size || ""} onChange={(e) => update({ project_size: e.target.value })} data-testid="project-size-input" />
            </div>
            <div>
              <Label>Valid (days)</Label>
              <Input type="number" value={proposal.valid_days || 30} onChange={(e) => update({ valid_days: parseInt(e.target.value) || 30 })} />
            </div>
          </CardContent>
        </Card>

        {/* Scope description */}
        <Card>
          <CardHeader><CardTitle>Scope Description</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              rows={5}
              placeholder="Describe what's included in plain language — Claude will polish this into formal scope-of-work prose."
              value={proposal.scope_description || ""}
              onChange={(e) => update({ scope_description: e.target.value })}
              data-testid="scope-description-input"
            />
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Line Items</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem("line_items", { id: crypto.randomUUID(), description: "", amount: 0 })} data-testid="add-line-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </CardHeader>
          <CardContent>
            {(proposal.line_items || []).map((li, idx) => (
              <div key={li.id || idx} className="flex gap-2 mb-2">
                <Input
                  placeholder="Description"
                  value={li.description || ""}
                  onChange={(e) => updateNested("line_items", idx, { description: e.target.value })}
                  className="flex-1"
                  data-testid={`line-item-desc-${idx}`}
                />
                <Input
                  type="number"
                  placeholder="Amount"
                  value={li.amount || ""}
                  onChange={(e) => updateNested("line_items", idx, { amount: parseFloat(e.target.value) || 0 })}
                  className="w-40"
                  data-testid={`line-item-amount-${idx}`}
                />
                <Button variant="ghost" onClick={() => removeItem("line_items", idx)} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <div className="border-t pt-3 mt-3 text-right">
              <span className="text-sm text-zinc-500">Total Lump Sum:</span>{" "}
              <span className="font-bold text-lg text-[#00341d]" data-testid="total-amount">
                ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Schedule</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem("schedule", { id: crypto.randomUUID(), phase: "", responsible: "Contractor", items: [] })} data-testid="add-schedule-phase-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Phase
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {(proposal.schedule || []).map((sch, idx) => (
              <div key={sch.id || idx} className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
                <div className="flex gap-2 items-center mb-2">
                  <Input
                    placeholder='e.g. "Week 1"'
                    value={sch.phase || ""}
                    onChange={(e) => updateNested("schedule", idx, { phase: e.target.value })}
                    className="flex-1"
                  />
                  <Select value={sch.responsible || "Contractor"} onValueChange={(v) => updateNested("schedule", idx, { responsible: v })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RESPONSIBILITIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" onClick={() => removeItem("schedule", idx)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <Textarea
                  rows={2}
                  placeholder="One item per line (will be rendered as bullets)"
                  value={(sch.items || []).join("\n")}
                  onChange={(e) => updateNested("schedule", idx, { items: e.target.value.split("\n").filter(Boolean) })}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Payment Schedule */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Payment Schedule</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem("payment_schedule", { id: crypto.randomUUID(), milestone: "", percent: 0, amount: 0 })}>
              <Plus className="w-4 h-4 mr-1" /> Add Milestone
            </Button>
          </CardHeader>
          <CardContent>
            {(proposal.payment_schedule || []).map((m, idx) => (
              <div key={m.id || idx} className="flex gap-2 mb-2">
                <Input placeholder="Milestone" value={m.milestone || ""} onChange={(e) => updateNested("payment_schedule", idx, { milestone: e.target.value })} className="flex-1" />
                <Input type="number" placeholder="%" value={m.percent || ""} onChange={(e) => updateNested("payment_schedule", idx, { percent: parseFloat(e.target.value) || 0 })} className="w-28" />
                <div className="w-40 flex items-center text-sm text-zinc-500 pl-3">
                  ${(totalAmount * (m.percent || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <Button variant="ghost" onClick={() => removeItem("payment_schedule", idx)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <p className="text-xs text-zinc-400 mt-2">Amount is auto-calculated from % of the line-items total.</p>
          </CardContent>
        </Card>

        {/* Checklist sections */}
        <ChecklistSection
          title="Pre-Construction (Developer responsibilities)"
          libraryKey="pre_construction_library"
          itemKey="pre_construction"
          proposal={proposal}
          settings={settings}
          setProposal={setProposal}
          onLibraryUpdate={fetchAll}
        />
        <ChecklistSection title="Inclusions" libraryKey="inclusions_library" itemKey="inclusions" proposal={proposal} settings={settings} setProposal={setProposal} onLibraryUpdate={fetchAll} />
        <ChecklistSection title="Exclusions" libraryKey="exclusions_library" itemKey="exclusions" proposal={proposal} settings={settings} setProposal={setProposal} onLibraryUpdate={fetchAll} />
        <ChecklistSection title="Allowances & Assumptions" libraryKey="assumptions_library" itemKey="assumptions" proposal={proposal} settings={settings} setProposal={setProposal} onLibraryUpdate={fetchAll} />

        {/* Insurance */}
        <Card>
          <CardHeader><CardTitle>Insurance Language</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              rows={3}
              value={proposal.insurance_text || ""}
              onChange={(e) => update({ insurance_text: e.target.value })}
              data-testid="insurance-text-input"
            />
          </CardContent>
        </Card>

        {/* Email notes */}
        <Card>
          <CardHeader><CardTitle>Special Notes for the Email</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              rows={3}
              placeholder="e.g. explain why you chose a 2-week phase, or any specific highlights for the client"
              value={proposal.email_notes || ""}
              onChange={(e) => update({ email_notes: e.target.value })}
              data-testid="email-notes-input"
            />
          </CardContent>
        </Card>

        {/* AI prompt */}
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader><CardTitle className="text-amber-800 flex items-center gap-2"><Sparkles className="w-5 h-5" /> Draft with Claude</CardTitle></CardHeader>
          <CardContent>
            <Label>Extra context for Claude (optional)</Label>
            <Textarea
              rows={2}
              placeholder="e.g. Emphasize the 2-week phase, note that burn permit is confirmed, client is a repeat developer..."
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              data-testid="ai-extra-context-input"
            />
            <Button
              onClick={handleDraft}
              disabled={drafting}
              className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="draft-with-claude-btn-main"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {drafting ? "Claude is drafting..." : "Draft Proposal + Email"}
            </Button>
          </CardContent>
        </Card>

        {/* Generated AI output */}
        {proposal.ai && (proposal.ai.scope_of_work || proposal.ai.email_body) && (
          <Card className="border-emerald-200 bg-emerald-50/30" data-testid="ai-output-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-emerald-800">Claude Draft</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowEmail((s) => !s)}>
                <Eye className="w-4 h-4 mr-1" /> {showEmail ? "Hide" : "Show"} email draft
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {showEmail && proposal.ai.email_subject && (
                <div className="p-3 bg-white border border-emerald-200 rounded-md" data-testid="ai-email-draft">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Email Subject</p>
                  <p className="font-medium mb-3">{proposal.ai.email_subject}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Email Body</p>
                  <pre className="whitespace-pre-wrap font-sans leading-relaxed">{proposal.ai.email_body}</pre>
                </div>
              )}
              {["scope_of_work", "schedule", "price", "payment", "inclusions", "exclusions", "allowances_assumptions", "insurance", "acceptance"].map((k) => (
                proposal.ai[k] ? (
                  <details key={k} className="bg-white border border-zinc-200 rounded-md" data-testid={`ai-section-${k}`}>
                    <summary className="cursor-pointer p-3 font-medium uppercase text-xs tracking-wider text-[#00341d]">
                      {k.replace(/_/g, " ")}
                    </summary>
                    <pre className="whitespace-pre-wrap font-sans leading-relaxed p-3 pt-0">{proposal.ai[k]}</pre>
                  </details>
                ) : null
              ))}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// ---------- Checklist with library + per-proposal add/remove ----------
const ChecklistSection = ({ title, libraryKey, itemKey, proposal, settings, setProposal, onLibraryUpdate }) => {
  const [newText, setNewText] = useState("");
  const items = proposal[itemKey] || [];
  const libraryItems = settings?.[libraryKey] || [];

  const toggle = (idx, v) => {
    setProposal((p) => {
      const list = [...(p[itemKey] || [])];
      list[idx] = { ...list[idx], checked: v };
      return { ...p, [itemKey]: list };
    });
  };

  const remove = (idx) => setProposal((p) => {
    const list = [...(p[itemKey] || [])];
    list.splice(idx, 1);
    return { ...p, [itemKey]: list };
  });

  const addNew = async () => {
    const t = newText.trim();
    if (!t) return;
    // 1) add to this proposal
    setProposal((p) => ({
      ...p,
      [itemKey]: [...(p[itemKey] || []), { id: crypto.randomUUID(), text: t, checked: true }],
    }));
    setNewText("");
    // 2) persist to library for next time
    try {
      await axios.post(`${API}/proposal-settings/library/${libraryKey}`, { text: t, default_checked: true });
      onLibraryUpdate?.();
    } catch {
      // non-fatal: still added to the current proposal
    }
  };

  const addFromLibrary = (libItem) => {
    // skip if already in this proposal
    if (items.some((it) => it.text === libItem.text)) return;
    setProposal((p) => ({
      ...p,
      [itemKey]: [...(p[itemKey] || []), { id: crypto.randomUUID(), text: libItem.text, checked: true }],
    }));
  };

  const unusedLibrary = libraryItems.filter((lib) => !items.some((it) => it.text === lib.text));

  return (
    <Card data-testid={`checklist-${itemKey}`}>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && <p className="text-xs text-zinc-400">No items added yet.</p>}
        {items.map((item, idx) => (
          <div key={item.id || idx} className="flex items-start gap-2">
            <Checkbox
              checked={item.checked}
              onCheckedChange={(v) => toggle(idx, !!v)}
              className="mt-1"
              data-testid={`${itemKey}-checkbox-${idx}`}
            />
            <span className="flex-1 text-sm">{item.text}</span>
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => remove(idx)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}

        {unusedLibrary.length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-zinc-400 mb-1.5">Reuse from library:</p>
            <div className="flex flex-wrap gap-1.5">
              {unusedLibrary.map((lib) => (
                <button
                  key={lib.id}
                  type="button"
                  onClick={() => addFromLibrary(lib)}
                  className="text-xs px-2 py-1 rounded-md border border-[#00341d]/30 bg-[#00341d]/5 text-[#00341d] hover:bg-[#00341d]/10"
                  data-testid={`library-reuse-${itemKey}`}
                >
                  + {lib.text.length > 60 ? lib.text.slice(0, 60) + "…" : lib.text}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-zinc-100">
          <Input
            placeholder="Add new item (also saved to library for next time)..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNew())}
            data-testid={`${itemKey}-new-input`}
          />
          <Button variant="outline" onClick={addNew} data-testid={`${itemKey}-add-btn`}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProposalBuilderPage;
